import { EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tippy } from '@tippyjs/react';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { resolveApiMessage, showToast } from '../../../helpers';
import { getOpsCalendar, getOpsSchedulingMeta } from '../../../requests';
import {
  addCalendarBlock,
  getCalendarBlocks,
  getFreeSlots,
  getOpsInstructorIdentities,
  getReservationDrift,
  getReservationSettings,
  removeCalendarBlock,
  TCalendarBlock,
} from '../../../requests/opsScheduling';
import { TOpsCalendarEvent } from '../../../types';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  PageHeader,
  Pill,
  Toolbar,
} from '../ui';

// GoMeddo's ten workflow statuses, colored by how settled the class is.
const STATUS_COLORS: Record<string, string> = {
  'Fully Confirmed': '#2f6b48',
  'SME Confirmed': '#0e7490',
  'SME Requested': '#b45309',
  'Schedule Queue': '#6d28d9',
  Draft: '#64748b',
  Hold: '#a16207',
  'Hold Expired': '#9a3412',
  Completed: '#334155',
  Canceled: '#b91c1c',
  Expired: '#737373',
  // Non-teaching blocks
  pto: '#7c2d12',
  prep: '#1e3a8a',
  travel: '#3f6212',
  content: '#4c1d95',
  meeting: '#374151',
  holiday: '#9f1239',
  other: '#52525b',
};

const BLOCK_KINDS = [
  'pto',
  'prep',
  'travel',
  'content',
  'meeting',
  'holiday',
  'other',
];
/** A checkbox list behind a button: pick several values at once. */
const MultiPick: React.FC<{
  label: string;
  options: { value: string; label: string; count?: number }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const shown = options.filter(
    (o) => !q || o.label.toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div className="relative">
      <button
        type="button"
        className={`${inputClass} flex items-center gap-1.5 ${
          selected.size ? 'border-blue-primary text-blue-primary' : ''
        }`}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {selected.size > 0 ? ` · ${selected.size}` : ''}
        <span className="text-[10px]">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-72 rounded-md border border-white-three bg-white p-2 shadow-light">
          {options.length > 8 && (
            <input
              className={`${inputClass} mb-1 w-full`}
              autoFocus
              placeholder="Filter…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
          <div className="max-h-64 overflow-y-auto">
            {shown.length === 0 && (
              <div className="px-1 py-1 text-xs text-gray-bold">
                Nothing in this view
              </div>
            )}
            {shown.map((o) => (
              <label
                key={o.value}
                className="flex items-center gap-2 px-1 py-1 text-sm text-gray-five hover:bg-blue-100/40"
              >
                <input
                  type="checkbox"
                  className="ops-check"
                  checked={selected.has(o.value)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(o.value);
                    else next.delete(o.value);
                    onChange(next);
                  }}
                />
                <span className="flex-1 truncate">{o.label}</span>
                {o.count != null && (
                  <span className="text-xs text-gray-bold">{o.count}</span>
                )}
              </label>
            ))}
          </div>
          <div className="mt-1 flex justify-between border-t border-white-three pt-1 text-xs">
            <button
              type="button"
              className="text-blue-primary"
              onClick={() => onChange(new Set())}
            >
              All
            </button>
            <button
              type="button"
              className="text-gray-bold"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toISOString().slice(0, 16).replace('T', ' ') : '—';

/**
 * Non-teaching time on the shared calendar — GoMeddo's "Create
 * Non-Teaching Activity". An instructor block takes their lane; a company
 * block (holiday) flags every class inside it.
 */
const BlocksPanel: React.FC<{ range: TRange | null }> = ({ range }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('pto');
  const [instructorId, setInstructorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const { data: blocks = [] } = useQuery({
    queryKey: ['opsCalendarBlocks', range?.from, range?.to],
    queryFn: () => getCalendarBlocks(range!.from, range!.to),
    enabled: !!range,
  });
  const { data: identities = [] } = useQuery({
    queryKey: ['opsInstructorIdentities'],
    queryFn: getOpsInstructorIdentities,
    enabled: open,
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['opsCalendarBlocks'] });
    queryClient.invalidateQueries({ queryKey: ['opsCalendar'] });
  };
  const add = useMutation({
    mutationFn: () =>
      addCalendarBlock({
        kind,
        instructorId: instructorId || null,
        from,
        to: to || null,
        note: note || null,
      }),
    onSuccess: (b: TCalendarBlock) => {
      const n = b.classesInside?.length ?? 0;
      showToast(
        n
          ? `Block added — ${n} class${n === 1 ? '' : 'es'} sit inside it`
          : 'Block added',
        n ? 'error' : 'success',
      );
      setOpen(false);
      setNote('');
      refresh();
    },
    onError: (err: unknown) => showToast(resolveApiMessage(err), 'error'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeCalendarBlock(id),
    onSuccess: refresh,
    onError: (err: unknown) => showToast(resolveApiMessage(err), 'error'),
  });
  const companyKind = ['holiday', 'meeting', 'other'].includes(kind);
  return (
    <Card>
      <div className="px-5 py-4 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className={labelClass}>
            Non-teaching time · {blocks.length} in view
          </div>
          {!open && (
            <button className={btnSecondary} onClick={() => setOpen(true)}>
              Block time
            </button>
          )}
        </div>
        {open && (
          <div className="mt-3 grid gap-2 rounded-lg border border-white-three bg-grey-50 p-3 md:grid-cols-5">
            <div>
              <label className={labelClass}>Kind</label>
              <select
                className={`${inputClass} w-full`}
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                {BLOCK_KINDS.map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Instructor</label>
              <select
                className={`${inputClass} w-full`}
                value={instructorId}
                onChange={(e) => setInstructorId(e.target.value)}
              >
                <option value="">
                  {companyKind ? 'Everyone (company-wide)' : 'Pick one'}
                </option>
                {identities.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>From</label>
              <input
                className={`${inputClass} w-full`}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>To</label>
              <input
                className={`${inputClass} w-full`}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Note</label>
              <input
                className={`${inputClass} w-full`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="flex gap-2 md:col-span-5">
              <button
                className={btnPrimary}
                disabled={
                  !from || (!companyKind && !instructorId) || add.isPending
                }
                onClick={() => add.mutate()}
              >
                Add block
              </button>
              <button className={btnGhost} onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {blocks.length > 0 && (
          <ul className="mt-3 divide-y divide-white-three rounded-lg border border-white-three text-xs">
            {blocks.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span>
                  <Pill value={b.kind} />{' '}
                  <span className="font-medium text-gray-four">
                    {b.instructorName ?? 'Everyone'}
                  </span>
                  <span className="text-gray-bold">
                    {' '}
                    · {fmt(b.from).slice(0, 10)} → {fmt(b.to).slice(0, 10)}
                    {b.note ? ` · ${b.note}` : ''}
                  </span>
                </span>
                <button
                  className={btnGhost}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(b.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

/** Dates for a named instructor under a recurring constraint (T3.06). */
const FreeSlotsPanel: React.FC<{
  resources: { id: string; name: string }[];
}> = ({ resources }) => {
  const [instructor, setInstructor] = useState('');
  const [hours, setHours] = useState('6');
  const [days, setDays] = useState<number[]>([2, 4]);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(
    new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10),
  );
  const [go, setGo] = useState(0);
  const { data, isFetching } = useQuery({
    queryKey: ['opsFreeSlots', instructor, hours, days.join(','), from, to, go],
    queryFn: () =>
      getFreeSlots({
        instructor,
        from,
        to,
        hours: Number(hours),
        weekdays: days,
      }),
    enabled: go > 0 && Boolean(instructor),
  });
  return (
    <Card>
      <div className="px-5 py-4 text-sm">
        <div className={labelClass}>Find dates for an instructor</div>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <label className={labelClass}>Instructor</label>
            <select
              className={inputClass}
              value={instructor}
              onChange={(e) => setInstructor(e.target.value)}
            >
              <option value="">Pick one</option>
              {resources.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Hours</label>
            <input
              className={`${inputClass} w-20`}
              type="number"
              min={1}
              max={12}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Weekdays</label>
            <div className="flex gap-1">
              {WEEKDAYS.map((w, i) => (
                <button
                  key={w}
                  type="button"
                  className={`rounded px-2 py-1 text-xs ${
                    days.includes(i)
                      ? 'bg-blue-primary text-white'
                      : 'bg-white-two text-gray-five'
                  }`}
                  onClick={() =>
                    setDays((d) =>
                      d.includes(i)
                        ? d.filter((x) => x !== i)
                        : [...d, i].sort(),
                    )
                  }
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>From</label>
            <input
              className={inputClass}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>To</label>
            <input
              className={inputClass}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <button
            className={btnSecondary}
            disabled={!instructor || isFetching}
            onClick={() => setGo((g) => g + 1)}
          >
            Find
          </button>
        </div>
        {data && (
          <div className="mt-3 text-xs">
            <div className="text-gray-bold">
              {data.windows.length} window{data.windows.length === 1 ? '' : 's'}{' '}
              of {data.hours}h for {data.instructor}
              {data.matchedIdentity
                ? ''
                : ' (name not in the identity map — spellings may be missed)'}{' '}
              · avoiding {data.busyCount} busy slot
              {data.busyCount === 1 ? '' : 's'}
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {data.windows.slice(0, 40).map((w) => (
                <Link
                  key={w.start}
                  className="rounded border border-white-three px-2 py-1 hover:bg-blue-100/60"
                  to={`${PATH.opsClassNew}?date=${w.start.slice(
                    0,
                    10,
                  )}&instructor=${encodeURIComponent(data.instructor)}`}
                >
                  {WEEKDAYS[new Date(w.start).getUTCDay()]}{' '}
                  {w.start.slice(0, 10)} · {w.start.slice(11, 16)}–
                  {w.end.slice(11, 16)} UTC
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

type TRange = { from: string; to: string };

const OpsCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<TRange | null>(null);
  // Multi-select filters (Eddie + Elizabeth, 2026-09-25): several types,
  // statuses or instructors at once; blocks (PTO, prep, holidays) can be
  // hidden. An empty set means "all".
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [resourceFilter, setResourceFilter] = useState<Set<string>>(new Set());
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [showBlocks, setShowBlocks] = useState(true);

  const { data: meta } = useQuery({
    queryKey: ['opsSchedulingMeta'],
    queryFn: getOpsSchedulingMeta,
  });
  const { data: settings } = useQuery({
    queryKey: ['opsReservationSettings'],
    queryFn: getReservationSettings,
  });
  const { data: drift } = useQuery({
    queryKey: ['opsReservationDrift'],
    queryFn: () => getReservationDrift(120),
    staleTime: 5 * 60 * 1000,
  });

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['opsCalendar', range?.from, range?.to],
    queryFn: () => getOpsCalendar(range!.from, range!.to),
    enabled: !!range,
  });

  // What is actually in the loaded range, so the pickers only offer values
  // that would show something (the mirror's "Private On-Site Course" type
  // has no upcoming reservations at all).
  const counts = useMemo(() => {
    const by = (k: (e: TOpsCalendarEvent) => string | null) => {
      const m = new Map<string, number>();
      for (const e of events) {
        const v = k(e);
        if (v) m.set(v, (m.get(v) ?? 0) + 1);
      }
      return m;
    };
    return {
      type: by((e) => (e.kind === 'block' ? null : e.type)),
      status: by((e) => (e.kind === 'block' ? null : e.status)),
      resource: by((e) => (e.kind === 'block' ? null : e.resourceId)),
    };
  }, [events]);

  const calendarEvents = useMemo<EventInput[]>(() => {
    return events
      .filter((e) => showBlocks || e.kind !== 'block')
      .filter(
        (e) =>
          e.kind === 'block' ||
          typeFilter.size === 0 ||
          typeFilter.has(e.type ?? ''),
      )
      .filter(
        (e) =>
          e.kind === 'block' ||
          statusFilter.size === 0 ||
          statusFilter.has(e.status ?? ''),
      )
      .filter(
        (e) =>
          resourceFilter.size === 0 ||
          resourceFilter.has(e.resourceId ?? '') ||
          (e.kind === 'block' && !e.resourceId),
      )
      .filter((e) => !conflictsOnly || e.hasConflicts)
      .map((e) => ({
        id: e.id,
        title: `${e.hasConflicts ? '⚠ ' : ''}${e.title}`,
        start: e.start,
        end: e.end,
        backgroundColor: STATUS_COLORS[e.status ?? ''] ?? '#475569',
        borderColor: 'transparent',
        extendedProps: e,
      }));
  }, [
    events,
    typeFilter,
    statusFilter,
    resourceFilter,
    conflictsOnly,
    showBlocks,
  ]);

  return (
    <>
      <PageHeader
        title="Delivery calendar"
        meta={
          settings?.source === 'portal'
            ? 'The portal is the source of truth for reservations. Click a date to schedule a class; click an event to open it'
            : 'Reservations still come from the GoMeddo mirror until cutover. Click a date to schedule a class; click an event to open it'
        }
      />
      <Toolbar>
        <MultiPick
          label="Types"
          options={(meta?.types ?? [])
            .map((t) => ({
              value: t.name,
              label: t.name,
              count: counts.type.get(t.name) ?? 0,
            }))
            .filter((o) => o.count > 0 || typeFilter.has(o.value))}
          selected={typeFilter}
          onChange={setTypeFilter}
        />
        <MultiPick
          label="Statuses"
          options={Array.from(counts.status.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([v, n]) => ({ value: v, label: v, count: n }))}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
        <MultiPick
          label="Instructors"
          options={(meta?.resources ?? [])
            .map((r) => ({
              value: r.id,
              label: `${r.name}${r.typeName ? ` (${r.typeName})` : ''}`,
              count: counts.resource.get(r.id) ?? 0,
            }))
            .filter((o) => o.count > 0 || resourceFilter.has(o.value))}
          selected={resourceFilter}
          onChange={setResourceFilter}
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-five">
          <input
            type="checkbox"
            className="ops-check"
            checked={conflictsOnly}
            onChange={(e) => setConflictsOnly(e.target.checked)}
          />
          Conflicts only
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-five">
          <input
            type="checkbox"
            className="ops-check"
            checked={showBlocks}
            onChange={(e) => setShowBlocks(e.target.checked)}
          />
          Show blocks (PTO, prep, holidays)
        </label>
        {(typeFilter.size > 0 ||
          statusFilter.size > 0 ||
          resourceFilter.size > 0) && (
          <button
            className={btnGhost}
            onClick={() => {
              setTypeFilter(new Set());
              setStatusFilter(new Set());
              setResourceFilter(new Set());
            }}
          >
            Clear filters
          </button>
        )}
        {isLoading && <LoadingSpinner />}
      </Toolbar>
      <div className="px-8 pb-40">
        {drift && !drift.skipped && drift.count > 0 && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <b>{drift.count}</b> upcoming class{drift.count === 1 ? '' : 'es'}{' '}
            where the class and its GoMeddo reservation disagree
            {drift.byField
              ? ` (${Object.entries(drift.byField)
                  .map(([k, v]) => `${k} ${v}`)
                  .join(', ')})`
              : ''}
            . Until cutover the reservation is what Salesforce reports on.{' '}
            <Link
              className="underline"
              to={`${PATH.opsReports}#reservationDrift`}
            >
              Work the list
            </Link>
          </div>
        )}
        <div className="mb-4 grid gap-4 lg:grid-cols-2">
          <BlocksPanel range={range} />
          <FreeSlotsPanel
            resources={(meta?.resources ?? []).filter(
              (r) => r.typeName !== 'Not Booked',
            )}
          />
        </div>
        <div className="rounded-xl border border-white-regular bg-white p-4">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'dayGridMonth listWeek',
              center: 'title',
              right: 'prev today next',
            }}
            dayMaxEvents={true}
            dateClick={(arg) =>
              navigate(`${PATH.opsClassNew}?date=${arg.dateStr.slice(0, 10)}`)
            }
            eventClick={(info) => {
              const e = info.event.extendedProps as TOpsCalendarEvent;
              if (e.kind === 'block') return;
              if (e.eventRequestId) {
                navigate(PATH.opsClassEdit.replace(':id', e.eventRequestId));
              }
            }}
            datesSet={(arg) =>
              setRange({
                from: arg.view.activeStart.toISOString(),
                to: arg.view.activeEnd.toISOString(),
              })
            }
            events={calendarEvents}
            eventDidMount={(info) => {
              const e = info.event.extendedProps as TOpsCalendarEvent;
              tippy(info.el, {
                allowHTML: true,
                theme: 'light',
                content: tooltipHtml(e),
              });
            }}
          />
        </div>
      </div>
    </>
  );
};

// Why a class is flagged — the API says which; older payloads only had the flag.
type TCalendarEventWithReasons = TOpsCalendarEvent & {
  conflictReasons?: string[];
};

const REASON_TEXT: Record<string, string> = {
  gomeddo: '⚠ GoMeddo reports a conflict',
  'double-booked': '⚠ Instructor is booked on another class at this time',
  'hold-overlap':
    '◌ Two holds pencilled in on this instructor at the same time',
  'instructor-unavailable': '⚠ Instructor has marked these dates as away',
  'non-teaching-block': '⚠ Instructor has non-teaching time blocked here',
  'company-block': '⚠ Falls inside a company-wide block (holiday)',
};

function tooltipHtml(e: TCalendarEventWithReasons): string {
  const seats =
    e.maxPlaces != null
      ? `${e.registrations.total} / ${e.maxPlaces} registered`
      : `${e.registrations.total} registered`;
  const reasons = e.conflictReasons?.length
    ? e.conflictReasons.map((r) => REASON_TEXT[r] ?? `⚠ ${r}`)
    : e.hasConflicts
    ? ['⚠ Conflict']
    : [];
  if (e.kind === 'block') {
    return [
      e.title,
      e.resourceName ? 'Instructor block' : 'Company-wide block',
    ].join('<br/>');
  }
  const rows = [
    e.resourceName ?? 'No instructor assigned',
    [e.type, e.status].filter(Boolean).join(' · '),
    e.reservationStatus === 'Hold' && e.holdExpiresAt
      ? `Hold expires ${e.holdExpiresAt.slice(0, 16).replace('T', ' ')}`
      : '',
    e.localStart ? `${e.localStart} (${e.timezone ?? 'local'})` : '',
    seats,
    ...reasons,
    e.classNumber ? `Class ${e.classNumber}` : '',
  ];
  return rows.filter(Boolean).join('<br/>');
}

export default OpsCalendar;
